import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  FaPencil,
  FaPlus,
  FaFilter,
  FaTrashArrowUp,
  FaFolder,
  FaXmark,
  FaEye,
} from "react-icons/fa6";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import {
  useLazyGetAllFilterCategoryListQuery,
  useLazyGetAllDynamicFilterCategoryListQuery,
  useDeleteCategoryMutation,
  useAddAndUpdateCategoryMutation,
  useLazyGetCategoryProductCountQuery,
} from "./../../../Services/CategoryService";
import Breadcrumb from "../../../Components/BreadCrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Typography,
  Paper,
  useTheme,
  Avatar,
  FormControl,
  InputLabel,
  Box,
  TextField,
  Chip,
  TableSortLabel,
  Skeleton,
} from "@mui/material";
import { common } from "@mui/material/colors";
import OffCanvas from "../../../Components/OffCanvas";
import ChipStatus from "../../../Components/ChipStatus";
import AddCategory from "./AddCategory";
import { useConfirmation } from "../../../Components/ConfirmationDialog";
import { showSnackbar } from "./../../../Redux/Slices/NotificationSlice";
import { useDispatch, useSelector } from "react-redux";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  breadcrumbItems,
  parentCategories,
  statusOption,
  fullSortingOptions,
  chipStyle,
  commonSelectStyle,
} from "./../../../Helpers/Constant/CategoryContant";
import { CategoryFilterType } from "./../../../Helpers/Interfaces/FilterType";
import Utils from "../../../Helpers/Utils";
import CategorySlidingFilter from "../../../Components/SlidingFilter/CategorySlidingFilter";
import AppliedFilter from "../../../Components/AppliedFilter";
import dayjs from "dayjs";

// Combined state interface
interface CombinedState {
  // Pagination
  page: number;
  pageSize: number;
  // Filters
  parentCategory: string;
  status: string;
  search: string;
  sortBy: string;
  sortDirection: "asc" | "desc";
  startDate?: string;
  endDate?: string;
}

const Categories: React.FC = () => {
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [tempUpdateData, setTempUpdateData] = useState({});
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const useInfo = useSelector((state: any) => state.AuthSlice.adminAuthUser);
  const today = dayjs().format("YYYY-MM-DD");
  // console.log("39-usInfo",useInfo);
  // Combined state - single source of truth
  const [combinedState, setCombinedState] = useState<CombinedState>({
    // Pagination
    page: 0,
    pageSize: 10,
    // Filters
    parentCategory: "All",
    status: "All",
    search: "",
    sortBy: "",
    sortDirection: "desc",
    startDate: "",
    endDate: "",
  });

  // Debounce timer ref for search
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const isInitialMount = useRef(true);

  const [
    triggerGetCategoryList,
    { data: categoryList, error, isLoading, reset },
  ] = useLazyGetAllDynamicFilterCategoryListQuery();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { requestConfirmation } = useConfirmation();
  const [createCategory] = useAddAndUpdateCategoryMutation();
  const [getProductCount] = useLazyGetCategoryProductCountQuery();

  // Memoized function to build API filters
  const buildApiFilters = useCallback(() => {
    const apiFilters: any[] = [];

    // Status filter
    if (combinedState.status === "Archive") {
      apiFilters.push({ key: "isDelete", value: true });
    } else if (combinedState.status === "Active") {
      apiFilters.push({ key: "isActive", value: true });
      apiFilters.push({ key: "isDelete", value: false });
    } else if (combinedState.status === "Inactive") {
      apiFilters.push({ key: "isActive", value: false });
      apiFilters.push({ key: "isDelete", value: false });
    } else {
      // Default: show non-deleted items
      // apiFilters.push({ key: "isDelete", value: false });
    }

    // Parent category filter
    if (combinedState.parentCategory !== "All") {
      apiFilters.push({
        key: "parentCategoryName",
        value: combinedState.parentCategory,
      });
    }

    // Search filter
    if (combinedState.search.trim()) {
      apiFilters.push({
        key: "categoryName",
        value: combinedState.search.trim(),
      });
    }

    // Date range filters (pass raw YYYY-MM-DD as provided by the UI)
    if (combinedState.startDate) {
      apiFilters.push({ key: "startDate", value: combinedState.startDate });
    }
    if (combinedState.endDate) {
      apiFilters.push({ key: "endDate", value: combinedState.endDate });
    }

    return apiFilters;
  }, [
    combinedState.status,
    combinedState.parentCategory,
    combinedState.search,
    combinedState.startDate,
    combinedState.endDate,
  ]);

  // Memoized fetch function
  const fetchCategory = useCallback(() => {
    const baseParams = {
      page: combinedState.page + 1,
      pageSize: combinedState.pageSize,
      filters: buildApiFilters(),
      sortBy: combinedState.sortBy || undefined,
      sortOrder: combinedState.sortDirection || undefined,
    };

    reset();
    setFilteredData([]);
    triggerGetCategoryList(baseParams);
  }, [
    combinedState.page,
    combinedState.pageSize,
    buildApiFilters,
    combinedState.sortBy,
    combinedState.sortDirection,
    reset,
    triggerGetCategoryList,
  ]);

  // Single useEffect with debounced search
  useEffect(() => {
    // Skip initial mount to prevent unnecessary API call
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchCategory();
      return;
    }

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search changed, debounce the API call
    if (combinedState.search !== "") {
      searchTimeoutRef.current = setTimeout(() => {
        fetchCategory();
      }, 500); // 500ms debounce for search
    } else {
      // For non-search changes, call immediately
      fetchCategory();
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchCategory, combinedState.search]);

  // Update combined state helper
  const updateCombinedState = useCallback((updates: Partial<CombinedState>) => {
    setCombinedState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Handler functions
  const handleSort = useCallback(
    (column: string) => {
      updateCombinedState({
        sortBy: column,
        sortDirection:
          combinedState.sortBy === column
            ? combinedState.sortDirection === "asc"
              ? "desc"
              : "asc"
            : "asc",
        page: 0, // Reset page when sorting
      });
    },
    [combinedState.sortBy, combinedState.sortDirection, updateCombinedState],
  );

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      updateCombinedState({ page: newPage });
    },
    [updateCombinedState],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateCombinedState({
        page: 0,
        pageSize: parseInt(event.target.value, 10),
      });
    },
    [updateCombinedState],
  );

  const handleFilterChange = useCallback(
    (field: keyof Omit<CombinedState, "page" | "pageSize">) =>
      (event: SelectChangeEvent) => {
        updateCombinedState({
          [field]: event.target.value,
          page: 0, // Reset page when filters change
        });
      },
    [updateCombinedState],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateCombinedState({
        search: event.target.value,
        page: 0, // Reset page when search changes
      });
    },
    [updateCombinedState],
  );

  const handleReset = useCallback(() => {
    setCombinedState({
      page: 0,
      pageSize: 10,
      parentCategory: "All",
      status: "All",
      search: "",
      sortBy: "",
      sortDirection: "desc",
      startDate: "",
      endDate: "",
    });
  }, []);

  useEffect(() => {
    if (categoryList?.data && categoryList?.data?.length) {
      setFilteredData(categoryList?.data);
    }
  }, [categoryList]);

  const handleCategoryToggle = () => {
    setTempUpdateData({});
    setIsAddCategoryOpen(!isAddCategoryOpen);
  };

  const handleDelete = async (item: any) => {
    const id = item?._id;

    let message = item?.isDelete
      ? "Are you sure you want to unarchive this category?"
      : "Are you sure you want to archive (soft-delete) this category?";

    if (!item?.isDelete) {
      try {
        const result = await getProductCount(id).unwrap();
        if (result.count > 0) {
          message =
            "Are you sure you want to archive this category? It is assigned to products, and those products may no longer be visible in the customer portal if this is their only category";
        }
      } catch {
        // proceed with default message if count fetch fails
      }
    }

    requestConfirmation({
      title: `${item?.isDelete ? "Unarchive" : "Archive"} Category`,
      message,
      onConfirm: async () => {
        try {
          await createCategory({
            id: id,
            data: {
              parentCategoryName: item.parentCategoryName,
              categoryName: item.categoryName,
              description: item.description,
              isActive: false,
              icon: item.icon,
              isDelete: item.isDelete ? false : true,
            },
          }).unwrap();

          setFilteredData((prev) => prev.filter((item) => item._id !== id));
          dispatch(
            showSnackbar({
              message: `${item?.isDelete ? "Unarchived" : "Archived"} Category successfully`,
              severity: "success",
            }),
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: `${item?.isDelete ? "Unarchived" : "Archived"} Category failed`,
              severity: "error",
            }),
          );
        }
      },
    });
  };

  // Check if filters are active
  const hasActiveFilters =
    combinedState.search !== "" ||
    combinedState.parentCategory !== "All" ||
    combinedState.status !== "All" ||
    !!combinedState.startDate ||
    !!combinedState.endDate;

  // Category sliding filter state
  const [filters, setFilters] = useState({
    parentCategory: "All",
    status: "All",
    search: "",
    startDate: "",
    endDate: "",
  });

  const handleSlidingFilterChange = (
    filterName: string,
    value: string | boolean,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleApplyFilters = (filtersToApply: any) => {
    // Update the combined state with applied filters
    updateCombinedState({
      page: 0, // Reset to first page when applying filters
      parentCategory:
        filtersToApply.parentCategory === "All"
          ? "All"
          : filtersToApply.parentCategory,
      status: filtersToApply.status === "All" ? "All" : filtersToApply.status,
      search: filtersToApply.search || "",
      // keep start/end as raw YYYY-MM-DD
      // (if needed we can wire to API later)
      ...(filtersToApply.startDate
        ? { startDate: filtersToApply.startDate }
        : { startDate: "" as any }),
      ...(filtersToApply.endDate
        ? { endDate: filtersToApply.endDate }
        : { endDate: "" as any }),
    });

    setFilterOpen(false); // Close the filter panel
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      parentCategory: "All",
      status: "All",
      search: "",
      startDate: "",
      endDate: "",
    };
    setFilters(clearedFilters);

    // Clear the combined state
    updateCombinedState({
      page: 0,
      parentCategory: "All",
      status: "All",
      search: "",
      startDate: "" as any,
      endDate: "" as any,
    });
  };

  // Derived filters object for AppliedFilter chips. Convert "All" to empty so chips hide default values
  const appliedFiltersForChips = {
    parentCategory:
      combinedState.parentCategory === "All"
        ? ""
        : combinedState.parentCategory,
    status: combinedState.status === "All" ? "" : combinedState.status,
    search: combinedState.search || "",
    startDate: (combinedState as any).startDate || "",
    endDate: (combinedState as any).endDate || "",
  };

  // Clear a single filter from chips and keep both combinedState and sliding filters in sync
  const handleClearSingleFilter = (filterKey: string) => {
    const updates: Partial<CombinedState> = { page: 0 };
    if (filterKey === "parentCategory") {
      updates.parentCategory = "All";
      setFilters((prev) => ({ ...prev, parentCategory: "All" }));
    } else if (filterKey === "status") {
      updates.status = "All";
      setFilters((prev) => ({ ...prev, status: "All" }));
    } else if (filterKey === "search") {
      updates.search = "";
      setFilters((prev) => ({ ...prev, search: "" }));
    } else if (filterKey === "startDate") {
      (updates as any).startDate = "";
      setFilters((prev) => ({ ...prev, startDate: "" }));
    } else if (filterKey === "endDate") {
      (updates as any).endDate = "";
      setFilters((prev) => ({ ...prev, endDate: "" }));
    }
    updateCombinedState(updates);
  };

  return (
    // <div className="container-lg container-md container-sm">
    <Box>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div style={{ flex: 1 }}>
          <Breadcrumb items={breadcrumbItems} name={"Category"} />
        </div>

        {/* <Box component="section" className="search-section filter-grid" sx={{ display: 'flex', gap: '10px' }}> */}
        {/* <Box
                    component="section"
                    className="search-section filter-grid"
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1.2,
                        alignItems: "center",
                        flexWrap: "nowrap",
                        justifyContent: "flex-end",
                        flexGrow: 1,
                    }}
                > */}
        <div
          style={{
            display: "flex",
            cursor: "pointer",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {/* Applied Filters Component */}
          {/* <Box sx={{ ml: 4 }}> */}
          <AppliedFilter
            filters={appliedFiltersForChips}
            onClearFilter={handleClearSingleFilter}
            onClearAll={handleClearFilters}
            filterLabels={{
              parentCategory: "Parent Category",
              status: "Status",
              search: "Search",
              startDate: "Start Date",
              endDate: "End Date",
            }}
            customFormatters={{
              startDate: (v: string) =>
                v ? dayjs(v).format("YYYY-MM-DD") : "",
              endDate: (v: string) => (v ? dayjs(v).format("YYYY-MM-DD") : ""),
            }}
          />
          {/* </Box> */}

          <Button
            color="secondary"
            size={"medium"}
            sx={{
              height: 34,
              textTransform: "capitalize",
              boxShadow: theme.shadows[1],
              mr: 2,
            }}
            variant="outlined"
            startIcon={<FaFilter size={16} />}
            onClick={() => setFilterOpen(true)}
          >
            &nbsp;<span>Apply Filters</span>
          </Button>
          {useInfo?.permissions?.categories?.edit && (
            <Button
              color="primary"
              onClick={handleCategoryToggle}
              size={"medium"}
              sx={{ height: 34, textTransform: "capitalize" }}
              variant="contained"
            >
              <FaPlus size={16} color={common.white} />
              &nbsp;Add Category
            </Button>
          )}

          {/* </Box>
           */}
        </div>
      </div>

      {/* <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}> */}
      <Paper style={{ flex: 2 }} elevation={3}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 220px)" }}>
          <Table stickyHeader size={"small"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Icons</TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1!important",
                    },
                  }}
                  sortDirection={
                    combinedState.sortBy === "categoryName"
                      ? combinedState.sortDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={combinedState.sortBy === "categoryName"}
                    direction={
                      combinedState.sortBy === "categoryName"
                        ? combinedState.sortDirection
                        : "asc"
                    }
                    onClick={() => handleSort("categoryName")}
                    hideSortIcon={false}
                  >
                    Category&nbsp;Name
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1!important",
                    },
                  }}
                  sortDirection={
                    combinedState.sortBy === "parentCategoryName"
                      ? combinedState.sortDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={combinedState.sortBy === "parentCategoryName"}
                    direction={
                      combinedState.sortBy === "parentCategoryName"
                        ? combinedState.sortDirection
                        : "asc"
                    }
                    onClick={() => handleSort("parentCategoryName")}
                  >
                    Parent&nbsp;Category
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    maxWidth: 300,
                    overflow: "hidden",
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1!important",
                    },
                  }}
                  sortDirection={
                    combinedState.sortBy === "description"
                      ? combinedState.sortDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={combinedState.sortBy === "description"}
                    direction={
                      combinedState.sortBy === "description"
                        ? combinedState.sortDirection
                        : "asc"
                    }
                    onClick={() => handleSort("description")}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Updated At</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>IsArchive</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                {useInfo?.permissions?.categories?.edit && (
                  <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>
                    Action
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(10)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(8)].map((_, colIndex) => (
                      <TableCell key={colIndex}>
                        {colIndex === 8 ? (
                          <IconButton size="small" disabled>
                            <FaEye size={16} />
                          </IconButton>
                        ) : (
                          <Skeleton
                            variant="text"
                            width={colIndex === 0 ? 30 : "80%"}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="error">
                      Failed to load categories.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : categoryList &&
                categoryList?.data &&
                Array.isArray(categoryList?.data) &&
                categoryList?.data?.length > 0 ? (
                categoryList?.data.map((item, index) => (
                  <TableRow key={item._id || index}>
                    <TableCell>
                      {combinedState.page * combinedState.pageSize + index + 1}
                    </TableCell>
                    <TableCell>
                      {item?.icon ? (
                        <Avatar
                          alt="Category Icon"
                          src={item.icon}
                          variant="rounded"
                          sx={{ width: 32, height: 32 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <FaFolder />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>{item.categoryName}</TableCell>
                    <TableCell>{item.parentCategoryName}</TableCell>
                    <TableCell sx={{ maxWidth: 300, p: 0.5 }}>
                      <Typography
                        variant={"inherit"}
                        sx={{
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 1,
                          textOverflow: "ellipsis",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          px: 1,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item?.createdAt
                        ? dayjs(item.createdAt).format("DD.MM.YYYY, HH:mm:ss")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {item?.updatedAt
                        ? dayjs(item.updatedAt).format("DD.MM.YYYY, HH:mm:ss")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <ChipStatus
                        status={item?.isDelete ? "active" : "error"}
                        label={item?.isDelete ? "True" : "False"}
                      />
                    </TableCell>
                    <TableCell>
                      <ChipStatus
                        status={item?.isActive ? "active" : "error"}
                        label={item?.isActive ? "Active" : "InActive"}
                      />
                    </TableCell>
                    {useInfo?.permissions?.categories?.edit && (
                      <TableCell>
                        <IconButton
                          color="primary"
                          sx={{ color: theme.palette.error.main }}
                          onClick={() => handleDelete(item)}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/icons/${item?.isDelete ? "unarchive.png" : "archive.png"}`}
                            title={item?.isDelete ? "Unarchive" : "Archive"}
                            alt="Delete"
                            style={{ width: 15, height: 15 }}
                          />
                        </IconButton>
                        <IconButton
                          color="primary"
                          sx={{ color: theme.palette.warning.main, ml: 0.1 }}
                          onClick={() => {
                            handleCategoryToggle();
                            setTempUpdateData(item);
                          }}
                        >
                          <FaPencil size={15} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2">No Records Found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ background: theme.palette.background.default }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={(categoryList && categoryList?.totalRecords) || 1}
            rowsPerPage={combinedState.pageSize}
            page={combinedState.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Box>
      </Paper>

      <CategorySlidingFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        filters={filters}
        onFilterChange={handleSlidingFilterChange}
      />

      {/* </div> */}

      <OffCanvas
        open={isAddCategoryOpen}
        onClose={handleCategoryToggle}
        title="Add New Category"
        width={400}
      >
        {isAddCategoryOpen && (
          <AddCategory
            open={isAddCategoryOpen}
            updateData={tempUpdateData}
            onClose={() => {
              fetchCategory();
              handleCategoryToggle();
            }}
          />
        )}
      </OffCanvas>
    </Box>
    // </div>
  );
};

export default Categories;
